import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Check, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import AuthShell from '@/components/layout/AuthShell';
import AuthSplit, { AuthField, PasswordInput, AuthSubmit, FormAlert, formatApiError } from '@/components/layout/AuthSplit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RULES = [
  { key: 'length', label: 'At least 12 characters', test: (p) => p.length >= 12 },
  { key: 'letter', label: 'A letter', test: (p) => /[^\W\d_]/u.test(p) },
  { key: 'digit', label: 'A number', test: (p) => /\d/.test(p) },
];

const SAMPLE_ROWS = [
  { sev: 'critical', name: 'Ransomware affiliate · initial access', meta: 'Finance · North America', serial: 'SAMPLE-1' },
  { sev: 'high', name: 'Exploited VPN appliance CVE', meta: 'All sectors · KEV listed', serial: 'SAMPLE-2' },
  { sev: 'medium', name: 'MFA fatigue campaign', meta: 'Healthcare · Global', serial: 'SAMPLE-3' },
];

/**
 * Opened from the link an administrator sends. First invites choose a password
 * and are signed in; reset links choose a new password and go back to sign-in.
 */
export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { signInWithToken } = useAuth();
  const [info, setInfo] = useState({ status: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/auth/invite/${encodeURIComponent(token)}`)
      .then((res) => alive && setInfo({ status: 'ready', ...res.data }))
      .catch((err) => alive && setInfo({ status: 'invalid', message: err.response?.data?.detail }));
    return () => { alive = false; };
  }, [token]);

  const checks = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(password) })), [password]);
  const matches = password.length > 0 && password === confirm;
  const valid = checks.every((c) => c.ok) && matches;
  const isReset = info.purpose === 'reset';

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/invite/accept`, { token, password });
      if (res.data.signed_in) {
        await signInWithToken(res.data.token, res.data.user);
        toast.success('Welcome to IntelliSecure');
        navigate('/home', { replace: true });
      } else {
        toast.success('Password updated. Sign in to continue.');
        navigate('/login', { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err, 'Could not set the password. Try again.').message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell variant="split">
      <AuthSplit>
        <AuthSplit.Brief
          sectionLabel="Welcome"
          headline="Your organization is ready,"
          emphasis="set your password."
          briefTitle="What you'll see · Sample"
          rows={SAMPLE_ROWS}
          quote="Briefs are matched to your industry, region and the technology your administrator described."
        />
        <AuthSplit.Panel backTo="/">
          {info.status === 'loading' && <p className="auth-intro" role="status">Checking your link…</p>}

          {info.status === 'invalid' && (
            <>
              <div className="auth-head">
                <div className="auth-eyebrow">Link problem</div>
                <h1 className="auth-title">This link <em>can't be used.</em></h1>
                <p className="auth-intro" data-testid="invite-invalid">
                  {info.message || 'This link is invalid or has expired.'} Links work once and expire after seven days.
                </p>
              </div>
              <div className="auth-switch">
                <span>Already set a password?</span>
                <Link to="/login" className="auth-link">Sign in <ArrowRight size={12} aria-hidden="true" /></Link>
              </div>
            </>
          )}

          {info.status === 'ready' && (
            <>
              <div className="auth-head">
                <div className="auth-eyebrow">{isReset ? 'Password reset' : 'Accept invitation'}</div>
                <h1 className="auth-title">
                  {isReset ? <>Choose a new <em>password.</em></> : <>Set up your <em>account.</em></>}
                </h1>
                <p className="auth-intro">
                  {info.company_name ? <><strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{info.company_name}</strong> · </> : null}
                  {info.email}
                </p>
              </div>

              <form onSubmit={submit} className="auth-form" aria-describedby={error ? 'invite-error' : undefined} noValidate>
                <FormAlert id="invite-error" message={error} />
                <input type="email" name="username" autoComplete="username" value={info.email} readOnly hidden />

                <AuthField id="new_password" label="New password">
                  <PasswordInput id="new_password" name="new_password" data-testid="invite-password" autoComplete="new-password"
                                 value={password} onChange={(e) => setPassword(e.target.value)} required />
                </AuthField>
                <ul className="auth-rules" aria-label="Password requirements" style={{ listStyle: 'none', margin: '-4px 0 0', padding: 0, display: 'grid', gap: 4, fontSize: 12 }}>
                  {checks.map((c) => (
                    <li key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6, color: c.ok ? 'var(--state-success)' : 'var(--text-muted)' }}>
                      {c.ok ? <Check size={13} aria-hidden="true" /> : <Circle size={13} aria-hidden="true" />}
                      {c.label}<span className="sr-only">{c.ok ? ' (met)' : ' (not met yet)'}</span>
                    </li>
                  ))}
                </ul>

                <AuthField id="confirm_password" label="Confirm password" error={confirm && !matches ? 'The passwords do not match.' : undefined}>
                  <PasswordInput id="confirm_password" name="confirm_password" data-testid="invite-confirm" autoComplete="new-password"
                                 value={confirm} onChange={(e) => setConfirm(e.target.value)} invalid={Boolean(confirm) && !matches} required />
                </AuthField>

                <AuthSubmit data-testid="invite-submit" loading={loading} loadingLabel="Saving…" disabled={!valid}>
                  {isReset ? 'Save new password' : 'Create my account'}
                  <ArrowRight aria-hidden="true" />
                </AuthSubmit>
              </form>
            </>
          )}
        </AuthSplit.Panel>
      </AuthSplit>
    </AuthShell>
  );
}
